import React, { useCallback, useEffect, useRef, useState } from "react";
import { LayoutTemplate, Loader2, Plus, Trash2, RotateCcw, Save, Languages, Upload, ArrowUp, ArrowDown, Eye, GripVertical } from "lucide-react";
import DOMPurify from "dompurify";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";
import { RichText } from "./RichText";
import { SOCIAL_PLATFORMS, socialIcon, socialLabel } from "./SiteFooter";

const LANGS = [["en", "English"], ["es", "Español"], ["fr", "Français"], ["pt", "Português"], ["de", "Deutsch"]];
const TRANSITIONS = [["fade", "Fade"], ["slide", "Slide"], ["none", "No animation"]];
const HOME_SECTIONS = [["categories", "Shop by category"],
  ["featured", "Featured deals"], ["nearby", "Deals near you"], ["values", "Why LytePress"]];

const FOOTER_ROLES = [["guest", "Guests"], ["shopper", "Shoppers"], ["business", "Businesses"], ["admin", "Admins"]];
const FOOTER_DEFAULTS = () => ({
  tagline: "Save More. Waste Less.",
  links: [
    { id: "our-story", label: "Our Story", href: "/our-story", roles: [], on: true },
    { id: "cities", label: "Cities", href: "/cities", roles: [], on: true },
    { id: "contact", label: "Contact", href: "/contact-us", roles: [], on: true },
    { id: "pricing", label: "Pricing", href: "/pricing", roles: ["business", "admin"], on: true },
    { id: "terms", label: "Terms of Use", href: "/terms", roles: [], on: true },
    { id: "privacy", label: "Privacy Policy", href: "/privacy", roles: [], on: true },
  ],
});

const field = "w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none";

const ImageField = ({ value, onChange, testid }) => {
  const { toast } = useToast();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url || data.path);
    } catch { toast({ title: "Upload failed" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL"
        data-testid={testid} className={`${field} flex-1 min-w-[220px]`} />
      <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-upload`}
        className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />} Upload
      </button>
      {value && <img src={value} alt="" className="h-10 w-16 object-cover rounded-lg border border-border" />}
    </div>
  );
};

// The homepage and Our Story, editable without a deploy — English is the source, the rest is translated.
// Pass only="footer" to render just the footer editor as its own standalone card.
export const AdminContent = ({ only = null }) => {
  const footerOnly = only === "footer";
  const tid = (n) => (footerOnly ? `footer-${n}` : n);
  const { toast } = useToast();
  const [kind, setKind] = useState(footerOnly ? "footer" : "home");
  const [lang, setLang] = useState("en");
  const [d, setD] = useState({});
  const [translated, setTranslated] = useState([]);
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);
  const [openCopy, setOpenCopy] = useState("");
  const [preview, setPreview] = useState(false);

  const load = useCallback((k, l) => api.get(`/content/${k}?lang=${l}`).then(({ data }) => {
    let dd = data.data || {};
    if (k === "footer" && l === "en" && !(dd.links && dd.links.length)) dd = FOOTER_DEFAULTS();
    setD(dd); setTranslated(data.translated || []); setDirty(false);
  }).catch(() => setD({})), []);
  useEffect(() => { load(kind, lang); }, [kind, lang, load]);

  const set = (patch) => { setDirty(true); setD((x) => ({ ...x, ...patch })); };
  const slides = d.slides || [];
  const setSlide = (i, patch) => set({ slides: slides.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const fLinks = d.links || [];
  const isEn = lang === "en";
  const setLink = (i, patch) => set({ links: fLinks.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const removeLink = (i) => set({ links: fLinks.filter((_, j) => j !== i) });
  const addLink = () => set({ links: [...fLinks, { id: Math.random().toString(36).slice(2, 10), label: "", href: "", roles: [], on: true }] });
  const reorder = (arr, from, to) => { const n = [...arr]; const [m] = n.splice(from, 1); n.splice(to, 0, m); return n; };
  const [dragLink, setDragLink] = useState(null);
  const [dragSocial, setDragSocial] = useState(null);
  const fSocials = d.socials || [];
  const setSocial = (i, patch) => set({ socials: fSocials.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const removeSocial = (i) => set({ socials: fSocials.filter((_, j) => j !== i) });
  const addSocial = () => set({ socials: [...fSocials, { id: Math.random().toString(36).slice(2, 10), platform: "facebook", href: "", on: true }] });
  const move = (i, dir) => {
    const next = [...(d.sections || HOME_SECTIONS.map(([id]) => ({ id, on: true })))];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    set({ sections: next });
  };
  const sectionRows = d.sections?.length
    ? d.sections.filter((r) => HOME_SECTIONS.some(([id]) => id === r.id))
    : HOME_SECTIONS.map(([id]) => ({ id, on: true }));

  const save = async () => {
    setBusy("save");
    try {
      await api.post(`/admin/content/${kind}`, { lang, data: d });
      setDirty(false);
      toast({ title: "Published",
        description: lang === "en" ? "Live now, and being translated into ES, FR, PT and DE."
          : `Saved the ${lang.toUpperCase()} version.` });
      if (lang === "en") setTimeout(() => load(kind, lang), 15000);
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not save" }); }
    finally { setBusy(""); }
  };

  const translate = async () => {
    setBusy("translate");
    try {
      const { data } = await api.post(`/admin/content/${kind}/translate`);
      toast({ title: `Translated into ${data.languages.join(", ").toUpperCase()}` });
      load(kind, lang);
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not translate" }); }
    finally { setBusy(""); }
  };

  const revert = async () => {
    if (!window.confirm("Drop your edits and go back to the built-in page?")) return;
    setBusy("revert");
    try {
      await api.post(`/admin/content/${kind}/revert`);
      setD({}); setTranslated([]);
      toast({ title: "Reverted to the built-in page" });
    } catch { toast({ title: "Could not revert" }); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid={footerOnly ? "admin-footer" : "admin-content"}>
      <h2 className="font-bold text-foreground flex items-center gap-2">
        <LayoutTemplate size={18} className="text-[#16a34a]" /> {footerOnly ? "Footer" : "Pages"}
      </h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">
        {footerOnly
          ? "Edit the footer tagline, its links (with per-role visibility) and the social icon row. English is the source: save it and the other languages are written for you."
          : "Leave a field blank to keep what's built in. English is the source: save it and the other languages are written for you, then hand-edit any of them here."}
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {!footerOnly && [["home", "Homepage"], ["story", "Our Story"], ["contact", "Contact"]].map(([k, label]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`content-page-${k}`}
            className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${kind === k
              ? "bg-[#0f2a1a] text-white border-[#0f2a1a]" : "bg-card text-foreground border-border hover:border-[#16a34a]"}`}>
            {label}
          </button>
        ))}
        <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="content-lang"
          className="bg-secondary rounded-xl px-3 py-2 text-xs font-bold text-foreground outline-none ml-auto">
          {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {kind === "footer" ? (
        <div className="space-y-4" data-testid="footer-editor">
          {!isEn && (
            <p className="text-xs font-semibold text-[#b8860b] bg-[#fdf6e3] dark:bg-[#3a2f10] border border-[#f5b942] rounded-xl px-3 py-2">
              You're editing the {lang.toUpperCase()} version — only labels and the tagline. Links,
              order and who-sees-what are set on the English footer.
            </p>
          )}
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Tagline — shows after “© {new Date().getFullYear()} LytePress ·”</label>
            <input value={d.tagline || ""} onChange={(e) => set({ tagline: e.target.value })}
              placeholder="Save More. Waste Less." data-testid="footer-tagline-input" className={`${field} mt-1`} />
          </div>

          <label className="block text-xs font-semibold text-muted-foreground pt-1">
            Links — set the label, where it goes, who sees it, and the order
          </label>
          {fLinks.length === 0 && (
            <p className="text-sm text-muted-foreground">No links yet. {isEn ? "Add one below." : "Save the English footer first."}</p>
          )}
          {fLinks.map((l, i) => (
            <div key={l.id || i}
              onDragOver={(e) => { if (isEn && dragLink !== null) e.preventDefault(); }}
              onDrop={(e) => { if (!isEn || dragLink === null) return; e.preventDefault(); if (dragLink !== i) set({ links: reorder(fLinks, dragLink, i) }); setDragLink(null); }}
              className={`border rounded-xl p-3 space-y-2 transition-colors ${dragLink === i ? "border-[#16a34a] bg-[#16a34a]/5" : "border-border"}`}
              data-testid={`footer-link-row-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                {isEn && (
                  <span draggable onDragStart={() => setDragLink(i)} onDragEnd={() => setDragLink(null)}
                    data-testid={`footer-link-drag-${i}`} title="Drag to reorder"
                    className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                    <GripVertical size={16} />
                  </span>
                )}
                <input value={l.label || ""} onChange={(e) => setLink(i, { label: e.target.value })}
                  placeholder="Label" data-testid={`footer-link-label-${i}`} className={`${field} font-semibold flex-1 min-w-[160px]`} />
                <label className="flex items-center gap-1 text-xs font-semibold text-muted-foreground whitespace-nowrap">
                  <input type="checkbox" checked={l.on !== false} disabled={!isEn}
                    onChange={(e) => setLink(i, { on: e.target.checked })}
                    data-testid={`footer-link-on-${i}`} className="h-4 w-4 accent-[#16a34a]" /> Show
                </label>
                <button onClick={() => removeLink(i)} disabled={!isEn} data-testid={`footer-link-remove-${i}`}
                  className="text-rose-600 border border-rose-500/40 hover:bg-rose-500/10 rounded-xl p-2 disabled:opacity-30"><Trash2 size={14} /></button>
              </div>
              <input value={l.href || ""} onChange={(e) => setLink(i, { href: e.target.value })} disabled={!isEn}
                placeholder="Destination — an internal page like /pricing or a full https:// URL"
                data-testid={`footer-link-href-${i}`} className={`${field} disabled:opacity-60`} />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-muted-foreground">Show to:</span>
                {FOOTER_ROLES.map(([r, label]) => {
                  const roles = l.roles || [];
                  const checked = roles.includes(r);
                  return (
                    <label key={r} className="flex items-center gap-1 text-xs font-semibold border border-border rounded-lg px-2 py-1">
                      <input type="checkbox" checked={checked} disabled={!isEn}
                        onChange={(e) => setLink(i, { roles: e.target.checked ? [...roles, r] : roles.filter((x) => x !== r) })}
                        data-testid={`footer-link-role-${i}-${r}`} className="h-3.5 w-3.5 accent-[#16a34a]" />
                      {label}
                    </label>
                  );
                })}
                {(l.roles || []).length === 0 && (
                  <span className="text-[11px] text-muted-foreground italic">Everyone sees it</span>
                )}
              </div>
            </div>
          ))}
          {isEn && (
            <button onClick={addLink} data-testid="footer-add-link"
              className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
              <Plus size={13} /> Add a link
            </button>
          )}

          {isEn && (
            <div className="pt-3 border-t border-border">
              <label className="block text-xs font-semibold text-muted-foreground">
                Social & app-store icons — an optional row shown under the links (same for everyone)
              </label>
              {fSocials.length === 0 && <p className="text-sm text-muted-foreground mt-1">No icons yet. Add one below.</p>}
              <div className="space-y-2 mt-2">
                {fSocials.map((s, i) => {
                  const Icon = socialIcon(s.platform);
                  return (
                    <div key={s.id || i}
                      onDragOver={(e) => { if (dragSocial !== null) e.preventDefault(); }}
                      onDrop={(e) => { if (dragSocial === null) return; e.preventDefault(); if (dragSocial !== i) set({ socials: reorder(fSocials, dragSocial, i) }); setDragSocial(null); }}
                      className={`flex flex-wrap items-center gap-2 border rounded-xl p-3 transition-colors ${dragSocial === i ? "border-[#16a34a] bg-[#16a34a]/5" : "border-border"}`}
                      data-testid={`footer-social-row-${i}`}>
                      <span draggable onDragStart={() => setDragSocial(i)} onDragEnd={() => setDragSocial(null)}
                        data-testid={`footer-social-drag-${i}`} title="Drag to reorder"
                        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                        <GripVertical size={16} />
                      </span>
                      <span className="h-8 w-8 grid place-items-center rounded-full border border-border text-[#16a34a] shrink-0"><Icon size={15} /></span>
                      <select value={s.platform || "website"} onChange={(e) => setSocial(i, { platform: e.target.value })}
                        data-testid={`footer-social-platform-${i}`}
                        className="bg-secondary rounded-xl px-3 py-2 text-sm font-semibold outline-none">
                        {SOCIAL_PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                      <input value={s.href || ""} onChange={(e) => setSocial(i, { href: e.target.value })}
                        placeholder="https://…" data-testid={`footer-social-href-${i}`} className={`${field} flex-1 min-w-[200px]`} />
                      <label className="flex items-center gap-1 text-xs font-semibold text-muted-foreground whitespace-nowrap">
                        <input type="checkbox" checked={s.on !== false} onChange={(e) => setSocial(i, { on: e.target.checked })}
                          data-testid={`footer-social-on-${i}`} className="h-4 w-4 accent-[#16a34a]" /> Show
                      </label>
                      <button onClick={() => removeSocial(i)} data-testid={`footer-social-remove-${i}`}
                        className="text-rose-600 border border-rose-500/40 hover:bg-rose-500/10 rounded-xl p-2"><Trash2 size={14} /></button>
                    </div>
                  );
                })}
              </div>
              <button onClick={addSocial} data-testid="footer-add-social"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
                <Plus size={13} /> Add an icon
              </button>
            </div>
          )}
        </div>
      ) : kind === "contact" ? (
        <div className="space-y-3" data-testid="contact-editor">
          <label className="flex items-center gap-2 text-xs font-semibold text-foreground border border-border rounded-xl px-3 py-2">
            <input type="checkbox" checked={!!d.hideInfo} onChange={(e) => set({ hideInfo: e.target.checked })}
              data-testid="contact-hide-info" className="h-4 w-4 accent-[#16a34a]" />
            Hide the whole contact details column — show only the form
          </label>
          <div className="grid sm:grid-cols-3 gap-2">
            {[["showEmail", "Show email"], ["showPhone", "Show phone"], ["showLocation", "Show location"],
              ["showHours", "Show opening hours"], ["showWhatsapp", "Show WhatsApp button"]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-xs font-semibold text-foreground border border-border rounded-xl px-3 py-2">
                <input type="checkbox" checked={d[k] !== false} disabled={!!d.hideInfo}
                  onChange={(e) => set({ [k]: e.target.checked })}
                  data-testid={`contact-toggle-${k}`} className="h-4 w-4 accent-[#16a34a]" />
                {label}
              </label>
            ))}
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Public contact email (shown on the page)</label>
            <input value={d.email || ""} onChange={(e) => set({ email: e.target.value })}
              placeholder="support@lytepress.com" data-testid="contact-email" className={`${field} mt-1`} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Public phone</label>
            <input value={d.phone || ""} onChange={(e) => set({ phone: e.target.value })}
              placeholder="+1 (868) 555-0123" data-testid="contact-phone" className={`${field} mt-1`} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Location line</label>
            <input value={d.location || ""} onChange={(e) => set({ location: e.target.value })}
              placeholder="Port of Spain, Trinidad & Tobago" data-testid="contact-location" className={`${field} mt-1`} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">
              Opening hours (one line per row, e.g. “Mon–Fri · 9am–5pm”)
            </label>
            <textarea value={d.hours || ""} onChange={(e) => set({ hours: e.target.value })} rows={3}
              placeholder={"Mon–Fri · 9am–5pm\nSat · 9am–1pm\nSun · closed"}
              data-testid="contact-hours" className={`${field} mt-1 resize-none`} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">
              WhatsApp number (digits and country code, e.g. 18685550123)
            </label>
            <input value={d.whatsapp || ""} onChange={(e) => set({ whatsapp: e.target.value })}
              placeholder="18685550123" data-testid="contact-whatsapp" className={`${field} mt-1`} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">
              Send form submissions to (kept private — leave blank to use the admin email)
            </label>
            <input value={d.formTo || ""} onChange={(e) => set({ formTo: e.target.value })}
              placeholder="inbox@lytepress.com" data-testid="contact-form-to" className={`${field} mt-1`} />
            <p className="text-xs text-muted-foreground mt-1">
              Every submission also lands in Admin → Inbox under Contact, whatever this is set to.
            </p>
          </div>
        </div>
      ) : kind === "story" ? (
        <div className="space-y-3">
          <input value={d.headline || ""} onChange={(e) => set({ headline: e.target.value })} placeholder="Page headline"
            data-testid="story-headline" className={field} />
          <input value={d.tagline || ""} onChange={(e) => set({ tagline: e.target.value })} placeholder="Tagline under the headline"
            data-testid="story-tagline" className={field} />
          <textarea value={d.mission || ""} onChange={(e) => set({ mission: e.target.value })} rows={2}
            placeholder="Mission line" data-testid="story-mission" className={`${field} resize-none`} />
          <label className="text-xs font-semibold text-muted-foreground">
            The story — write it like a real page (headings, bold, lists and links)
          </label>
          <RichText value={d.bodyHtml || ""} onChange={(v) => set({ bodyHtml: v })} testid="story-rich" minHeight={260} />
          <button
            onClick={() => set({ bodyHtml: String(d.body || "").split("\n\n").map((p) => `<p>${p.trim()}</p>`).join("") })}
            disabled={!d.body} data-testid="story-load-plain"
            className="self-start inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] disabled:opacity-40 px-3 py-2 rounded-xl transition-colors">
            <Plus size={13} /> Bring my earlier plain text into the editor
          </button>
          <label className="block text-xs font-semibold text-muted-foreground pt-1">Hero image</label>
          <ImageField value={d.image} onChange={(v) => set({ image: v })} testid="story-image" />
          <label className="text-xs font-semibold text-muted-foreground">Value cards</label>
          {(d.values || []).map((v, i) => (
            <div key={i} className="border border-border rounded-xl p-3 space-y-2" data-testid={`story-value-${i}`}>
              <div className="flex gap-2">
                <input value={v.title || ""} onChange={(e) => set({ values: d.values.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })}
                  placeholder="Card title" data-testid={`story-value-title-${i}`} className={`${field} font-semibold`} />
                <button onClick={() => set({ values: d.values.filter((_, j) => j !== i) })} data-testid={`story-value-remove-${i}`}
                  className="text-rose-600 border border-rose-500/40 hover:bg-rose-500/10 rounded-xl p-2"><Trash2 size={14} /></button>
              </div>
              <textarea value={v.body || ""} rows={2} onChange={(e) => set({ values: d.values.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)) })}
                placeholder="One line about it" data-testid={`story-value-body-${i}`} className={`${field} resize-none`} />
            </div>
          ))}
          <button onClick={() => set({ values: [...(d.values || []), { title: "", body: "" }] })} data-testid="story-add-value"
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
            <Plus size={13} /> Add a value card
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Slide transition</label>
              <select value={d.transition || "fade"} onChange={(e) => set({ transition: e.target.value })}
                data-testid="home-transition" className="mt-1 block bg-secondary rounded-xl px-3 py-2 text-sm font-semibold outline-none">
                {TRANSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Seconds per slide</label>
              <input type="number" min="2" max="30" value={Math.round((d.intervalMs || 5000) / 1000)}
                onChange={(e) => set({ intervalMs: Math.max(2, Number(e.target.value || 5)) * 1000 })}
                data-testid="home-interval" className="mt-1 block w-24 bg-secondary rounded-xl px-3 py-2 text-sm font-semibold outline-none" />
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-foreground mt-4">
              <input type="checkbox" checked={!!d.mapHomeOnMobile} onChange={(e) => set({ mapHomeOnMobile: e.target.checked })}
                data-testid="home-map-first" className="h-4 w-4 accent-[#16a34a]" />
              Map-first home on phones (classic layout stays one tap away)
            </label>
          </div>

          <label className="text-xs font-semibold text-muted-foreground">Hero slides</label>
          {slides.map((s, i) => (
            <div key={i} className="border border-border rounded-xl p-3 space-y-2" data-testid={`home-slide-${i}`}>
              <div className="flex gap-2">
                <input value={s.title || ""} onChange={(e) => setSlide(i, { title: e.target.value })}
                  placeholder="Headline" data-testid={`home-slide-title-${i}`} className={`${field} font-semibold`} />
                <button onClick={() => set({ slides: slides.filter((_, j) => j !== i) })} data-testid={`home-slide-remove-${i}`}
                  className="text-rose-600 border border-rose-500/40 hover:bg-rose-500/10 rounded-xl p-2"><Trash2 size={14} /></button>
              </div>
              <input value={s.subtitle || ""} onChange={(e) => setSlide(i, { subtitle: e.target.value })}
                placeholder="Subline" data-testid={`home-slide-sub-${i}`} className={field} />
              <div className="flex flex-wrap gap-2">
                <input value={s.badge || ""} onChange={(e) => setSlide(i, { badge: e.target.value })}
                  placeholder="Corner badge (e.g. Up to 70% off)" data-testid={`home-slide-badge-${i}`} className={`${field} flex-1`} />
                <input value={s.ctaLabel || ""} onChange={(e) => setSlide(i, { ctaLabel: e.target.value })}
                  placeholder="Button label" data-testid={`home-slide-cta-${i}`} className={`${field} flex-1`} />
              </div>
              <ImageField value={s.image} onChange={(v) => setSlide(i, { image: v })} testid={`home-slide-image-${i}`} />
            </div>
          ))}
          <button onClick={() => set({ slides: [...slides, { title: "", subtitle: "", badge: "", image: "" }] })}
            data-testid="home-add-slide"
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
            <Plus size={13} /> Add a slide
          </button>

          <label className="block text-xs font-semibold text-muted-foreground pt-2">
            Intro copy — a styled block right under the hero
          </label>
          <RichText value={d.introHtml || ""} onChange={(v) => set({ introHtml: v })} testid="home-intro-rich" minHeight={140} />

          <label className="block text-xs font-semibold text-muted-foreground pt-2">Sections — show, hide, order and write the copy</label>
          <div className="space-y-1.5">
            {sectionRows.map((row, i) => (
              <div key={row.id} className="border border-border rounded-xl px-3 py-2 space-y-2"
                data-testid={`home-section-${row.id}`}>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={row.on !== false} data-testid={`home-section-toggle-${row.id}`}
                    onChange={(e) => set({ sections: sectionRows.map((r, j) => (j === i ? { ...r, on: e.target.checked } : r)) })}
                    className="h-4 w-4 accent-[#16a34a]" />
                  <span className="text-sm font-semibold text-foreground flex-1">
                    {HOME_SECTIONS.find(([id]) => id === row.id)?.[1] || row.id}
                  </span>
                  <button onClick={() => setOpenCopy((c) => (c === row.id ? "" : row.id))}
                    data-testid={`home-section-copy-${row.id}`}
                    className={`text-xs font-bold rounded-lg px-2.5 py-1.5 border transition-colors ${(d.copy || {})[row.id]
                      ? "border-[#16a34a] text-[#16a34a]" : "border-border text-muted-foreground hover:border-[#16a34a]"}`}>
                    {openCopy === row.id ? "Close copy" : (d.copy || {})[row.id] ? "Edit copy" : "Add copy"}
                  </button>
                  <button onClick={() => move(i, -1)} data-testid={`home-section-up-${row.id}`} className="text-muted-foreground hover:text-foreground"><ArrowUp size={14} /></button>
                  <button onClick={() => move(i, 1)} data-testid={`home-section-down-${row.id}`} className="text-muted-foreground hover:text-foreground"><ArrowDown size={14} /></button>
                </div>
                {openCopy === row.id && (
                  <RichText value={(d.copy || {})[row.id] || ""} minHeight={120}
                    onChange={(v) => set({ copy: { ...(d.copy || {}), [row.id]: v } })}
                    testid={`home-copy-rich-${row.id}`} />
                )}
              </div>
            ))}
          </div>

          <label className="block text-xs font-semibold text-muted-foreground pt-2">
            Closing copy — a styled block at the bottom of the page
          </label>
          <RichText value={d.outroHtml || ""} onChange={(v) => set({ outroHtml: v })} testid="home-outro-rich" minHeight={140} />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        {dirty && (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#b8860b] bg-[#fdf6e3] dark:bg-[#3a2f10] border border-[#f5b942] rounded-xl px-3 py-2"
            data-testid={tid("content-unsaved")}>
            Unsaved changes — hit Save and publish
          </span>
        )}
        <button onClick={save} disabled={busy === "save"} data-testid={tid("content-save")}
          className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white px-3 py-2 rounded-xl transition-colors">
          {busy === "save" ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save and publish
        </button>
        <button onClick={translate} disabled={busy === "translate"} data-testid={tid("content-translate")}
          className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
          {busy === "translate" ? <Loader2 className="animate-spin" size={13} /> : <Languages size={13} />} Re-translate now
        </button>
        <button onClick={() => setPreview((p) => !p)} data-testid={tid("content-preview-toggle")}
          className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
          <Eye size={13} /> {preview ? "Hide preview" : "Preview the copy"}
        </button>
        <button onClick={revert} disabled={busy === "revert"} data-testid={tid("content-revert")}
          className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-rose-500/50 px-3 py-2 rounded-xl transition-colors">
          {busy === "revert" ? <Loader2 className="animate-spin" size={13} /> : <RotateCcw size={13} />} Revert to default
        </button>
      </div>
      {preview && kind === "footer" && (
        <div className="mt-3 border border-border rounded-2xl overflow-hidden" data-testid={tid("content-preview")}>
          <div className="bg-background p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground border-t border-border pt-4">
              <span>© {new Date().getFullYear()} LytePress · {d.tagline || "Save More. Waste Less."}</span>
              <nav className="flex flex-wrap items-center gap-x-5 gap-y-1">
                {(d.links || []).filter((l) => l.label && l.href && l.on !== false).map((l, i) => (
                  <span key={i} className="text-foreground">{l.label}</span>
                ))}
              </nav>
            </div>
            {(d.socials || []).filter((s) => s.href && s.on !== false).length > 0 && (
              <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-border" data-testid="footer-socials-preview">
                {(d.socials || []).filter((s) => s.href && s.on !== false).map((s, i) => {
                  const Icon = socialIcon(s.platform);
                  return (
                    <span key={i} title={socialLabel(s.platform)}
                      className="h-9 w-9 grid place-items-center rounded-full border border-border text-muted-foreground">
                      <Icon size={17} />
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <p className="bg-secondary px-5 py-2 text-xs text-muted-foreground">
            What each viewer sees depends on their role — this preview lists every link that's switched on.
          </p>
        </div>
      )}
      {preview && kind !== "contact" && kind !== "footer" && (
        <div className="mt-3 border border-border rounded-2xl overflow-hidden" data-testid="content-preview">
          {kind === "home" ? (
            <div className="bg-background p-5 space-y-4" data-testid="home-preview">
              <div className="relative overflow-hidden rounded-2xl bg-[#0f2a1a] text-white p-6">
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#16a34a]/40 blur-3xl" />
                <div className="relative">
                  <h3 className="text-2xl font-extrabold whitespace-pre-line">
                    {slides[0]?.title || "Great Deals. Less Waste."}
                  </h3>
                  <p className="text-sm text-emerald-100/80 mt-1">{slides[0]?.subtitle || "Your built-in hero copy"}</p>
                </div>
              </div>
              {d.introHtml && (
                <div className="lp-rich bg-card border border-border rounded-2xl p-5 text-sm text-muted-foreground"
                  data-testid="home-preview-intro" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(d.introHtml) }} />
              )}
              {sectionRows.filter((r) => r.on !== false).map((r) => (
                <div key={r.id} data-testid={`home-preview-section-${r.id}`}>
                  <p className="text-lg font-extrabold text-foreground">
                    {HOME_SECTIONS.find(([id]) => id === r.id)?.[1] || r.id}
                  </p>
                  {(d.copy || {})[r.id] ? (
                    <div className="lp-rich text-sm text-muted-foreground mt-1"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((d.copy || {})[r.id]) }} />
                  ) : (
                    <p className="text-xs text-muted-foreground/70 mt-1 italic">No copy — just the deals below it.</p>
                  )}
                  <div className="mt-2 h-16 rounded-xl border border-dashed border-border bg-secondary/40 grid place-items-center text-xs text-muted-foreground">
                    the section's own content sits here
                  </div>
                </div>
              ))}
              {d.outroHtml && (
                <div className="lp-rich bg-card border border-border rounded-2xl p-5 text-sm text-muted-foreground"
                  data-testid="home-preview-outro" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(d.outroHtml) }} />
              )}
            </div>
          ) : (
            <div className="bg-background p-5 space-y-4" data-testid="story-preview">
              <div className="relative overflow-hidden rounded-2xl text-white p-6 min-h-[120px] flex flex-col justify-end">
                {d.image && <img src={d.image} alt="" className="absolute inset-0 h-full w-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e6b3e]/95 to-[#0e6b3e]/30" />
                <div className="relative">
                  <h3 className="text-2xl font-extrabold">{d.headline || "Our Story"}</h3>
                  <p className="text-sm text-white/90">{d.tagline || "Save More. Waste Less. Support Local."}</p>
                </div>
              </div>
              {d.mission && <p className="text-base text-muted-foreground">{d.mission}</p>}
              {d.bodyHtml && (
                <div className="lp-rich text-sm text-muted-foreground" data-testid="story-preview-body"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(d.bodyHtml) }} />
              )}
            </div>
          )}
          <p className="bg-secondary px-5 py-2 text-xs text-muted-foreground">
            This is your copy exactly as it will read once you hit Save and publish.
          </p>
        </div>
      )}

      {translated.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2" data-testid={tid("content-translated")}>
          Translated: {translated.join(", ").toUpperCase()}
        </p>
      )}
    </div>
  );
};

export default AdminContent;
